// src/components/admin/AdminUsers.js
import React, { useState, useEffect, useRef } from 'react';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { secondaryAuth, storage } from '../../services/firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  getAllUsers,
  createUser,
  updateUser,
  unlockFitnessForUser,
  unlockLekhiForUser,
  pauseUserStreak,
  getLekhiTestResultsForUser,
  setLekhiSubjectLevel,
  deletePushSubscription
} from '../../services/firebase/firestoreService';
import { getSubjectName } from '../../services/lekhiTopicNames';
import { useAuth } from '../../context/AuthContext';
import ImageCropper from '../common/ImageCropper';

const LEVELS = [
  { value: 'beginnerBoys', label: 'Beginner Boys' },
  { value: 'beginnerGirls', label: 'Beginner Girls' },
  { value: 'advancedBoys', label: 'Advanced Boys' },
  { value: 'advancedGirls', label: 'Advanced Girls' },
  { value: 'specialBoys', label: 'Special Boys' },
  { value: 'specialGirls', label: 'Special Girls' },
];

const LEKHI_LEVEL_LABELS_MR = { easy: 'प्रारंभ', medium: 'प्रगत', hard: 'प्रावीण्य' };

const EMPTY_USER = {
  phone: '', firstName: '', lastName: '', village: '', fitnessLevel: 'beginnerBoys',
  dateOfBirth: '', password: '', fitnessStartDate: ''
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const REG_SHEET_ID = '1bbxZNGQd530WLK822NcppvZUf1rwSGRBOAIFeHaSEqs';
const REG_SHEET_GID = '1936293510';
const REG_SHEET_CSV_URL = `https://docs.google.com/spreadsheets/d/${REG_SHEET_ID}/gviz/tq?tqx=out:csv&gid=${REG_SHEET_GID}`;

const safeDisplay = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value || '—';
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value?.seconds) return new Date(value.seconds * 1000).toLocaleDateString('en-IN');
  return '—';
};

// Test points live inside lekhiPoints (+2 per test), so Lekhi Points shows
// the daily-session portion only. Fitness + Lekhi + Test = Total.
const getTotalPoints = (user) => (user.fitnessPoints || 0) + (user.lekhiPoints || 0);

const getTestPoints = (user) => (user.lekhiTestsTaken || 0) * 2;

const getLekhiDailyPoints = (user) => Math.max(0, (user.lekhiPoints || 0) - getTestPoints(user));

const getDaysSinceStart = (user) => {
  const raw = user.fitnessStartDate;
  if (!raw) return null;
  let startMs;
  if (raw?.seconds) {
    startMs = raw.seconds * 1000;
  } else {
    const parsed = new Date(raw);
    if (isNaN(parsed.getTime())) return null;
    startMs = parsed.getTime();
  }
  const d = new Date(startMs);
  const startStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const start = new Date(`${startStr}T00:00:00`);
  const today = new Date(`${getIndiaDateString()}T00:00:00`);
  const diff = Math.round((today - start) / 86400000);
  return diff < 0 ? 0 : diff;
};

const getIndiaDateString = () => {
  const now = new Date();
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const india = new Date(utc + 5.5 * 60 * 60 * 1000);
  const year = india.getFullYear();
  const month = String(india.getMonth() + 1).padStart(2, '0');
  const day = String(india.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ===== DETAILS PANEL HELPERS =====

const getLekhiLevelDisplay = (user, subject) => {
  const level = user[`${subject}Level`];
  if (!level) return '—';
  return LEKHI_LEVEL_LABELS_MR[level] || level;
};

const getFitnessAdherenceText = (user) => {
  const days = getDaysSinceStart(user);
  if (days === null) return '—';
  return `${user.fitnessPoints || 0}/${days} days`;
};

const getLekhiAdherenceText = (user) => {
  const days = getDaysSinceStart(user);
  if (days === null) return '—';
  return `${getLekhiDailyPoints(user)}/${days} days`;
};

const getDiagnosticChip = (user) => {
  if (user.diagnosticTotalScore === undefined || user.diagnosticTotalScore === null) return null;
  return `D1: ${user.diagnosticTotalScore}/32`;
};

const getCampsText = (user) => {
  const attended = (user.attendedCamps || []).slice().sort((a, b) => a - b);
  const unlocked = (user.unlockedCamps || []).slice().sort((a, b) => a - b);
  const pending = unlocked.filter(c => !attended.includes(c));
  const attendedStr = attended.length > 0 ? `✓${attended.join(',')}` : '✓—';
  const pendingStr = pending.length > 0 ? ` · Pending ${pending.join(',')}` : '';
  return attendedStr + pendingStr;
};

const uploadProfilePhoto = async (userId, file) => {
  const storageRef = ref(storage, `profile_photos/${userId}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

const removeProfilePhoto = async (userId) => {
  const storageRef = ref(storage, `profile_photos/${userId}`);
  await deleteObject(storageRef).catch(() => {});
  await updateUser(userId, { photoURL: '' });
};

// ===== REGISTRATION SHEET HELPERS =====

// Handles Google's gviz CSV export: quoted fields, embedded commas/newlines,
// doubled internal quotes.
const parseCSV = (text) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (char === '"' && next === '"') { field += '"'; i++; }
      else if (char === '"') { inQuotes = false; }
      else { field += char; }
    } else {
      if (char === '"') { inQuotes = true; }
      else if (char === ',') { row.push(field); field = ''; }
      else if (char === '\r') { /* skip */ }
      else if (char === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else { field += char; }
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
};

// Google Forms date answers typically land as DD/MM/YYYY in an India-locale
// sheet. If dates come out swapped on your sheet, flip dPart/mPart below —
// each fetched row's Date of Birth is editable either way, so a wrong parse
// is easy to spot and fix by eye before creating that user.
const parseSheetDate = (raw) => {
  const s = (raw || '').trim();
  if (!s) return '';
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const parts = s.split(/[/-]/);
  if (parts.length === 3) {
    let [dPart, mPart, yPart] = parts;
    if (dPart.length === 4) { [yPart, mPart, dPart] = parts; }
    if (yPart.length === 2) yPart = `20${yPart}`;
    const d = dPart.padStart(2, '0');
    const m = mPart.padStart(2, '0');
    if (yPart.length === 4) return `${yPart}-${m}-${d}`;
  }
  return '';
};

// Template: "fit" + digits at the 1st/3rd/5th/7th/9th position of the phone
// number, e.g. 9876543210 -> fit97531.
const generatePasswordFromPhone = (phone10) => {
  if (!phone10 || phone10.length !== 10) return '';
  let oddDigits = '';
  for (let i = 0; i < phone10.length; i += 2) oddDigits += phone10[i];
  return `fit${oddDigits}`;
};

export default function AdminUsers() {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_USER);
  const [newUserPhoto, setNewUserPhoto] = useState(null);
  const [newUserPhotoPreview, setNewUserPhotoPreview] = useState(null);
  const [villages, setVillages] = useState([]);
  const [showVillageInput, setShowVillageInput] = useState(false);
  const [newVillage, setNewVillage] = useState('');
  const [showEditVillageInput, setShowEditVillageInput] = useState(false);
  const [newEditVillage, setNewEditVillage] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'firstName', direction: 'asc' });
  const [statusMsg, setStatusMsg] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editPhoto, setEditPhoto] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [editPhotoUploading, setEditPhotoUploading] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [showPauseInput, setShowPauseInput] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropTarget, setCropTarget] = useState(null); // 'new' or 'edit'
  const [detailsUserId, setDetailsUserId] = useState(null);
  const [testResultsByUser, setTestResultsByUser] = useState({});
  const [testResultsLoadingId, setTestResultsLoadingId] = useState(null);

  // Registration sheet fetch state
  const [fetchedRows, setFetchedRows] = useState(null);
  const [fetchingSheet, setFetchingSheet] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [skippedCount, setSkippedCount] = useState(0);

  const newUserPhotoRef = useRef();
  const editPhotoRef = useRef();

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getAllUsers();
      const regular = data.filter(u => u.role !== 'admin');
      setUsers(regular);
      const uniqueVillages = [...new Set(regular.map(u => u.village).filter(Boolean))].sort();
      setVillages(uniqueVillages);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (text, type = 'success') => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg(null), 4000);
  };

  const getFullName = (user) => {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
  };

  const handleAddVillage = () => {
    const v = newVillage.trim();
    if (!v) return;
    if (villages.includes(v)) { showMsg('Village already exists', 'error'); return; }
    const updated = [...villages, v].sort();
    setVillages(updated);
    setNewUser({ ...newUser, village: v });
    setNewVillage('');
    setShowVillageInput(false);
  };

  const handleAddEditVillage = () => {
    const v = newEditVillage.trim();
    if (!v) return;
    if (villages.includes(v)) { showMsg('Village already exists', 'error'); return; }
    const updated = [...villages, v].sort();
    setVillages(updated);
    setEditForm({ ...editForm, village: v });
    setNewEditVillage('');
    setShowEditVillageInput(false);
  };

  // Photo picked in the "Add New User" form → validate → open cropper
  const handleNewUserPhotoChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) { showMsg('Photo must be under 10MB', 'error'); return; }
    setCropTarget('new');
    setCropImageSrc(URL.createObjectURL(file));
  };

  // Photo picked in the edit form → validate → open cropper
  const handleEditPhotoChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) { showMsg('Photo must be under 10MB', 'error'); return; }
    setCropTarget('edit');
    setCropImageSrc(URL.createObjectURL(file));
  };

  // Cropper returned a cropped square file → route it to the right form
  const handleCropComplete = (croppedFile) => {
    const srcToRevoke = cropImageSrc;
    const target = cropTarget;
    setCropImageSrc(null);
    setCropTarget(null);
    if (srcToRevoke) URL.revokeObjectURL(srcToRevoke);
    const previewURL = URL.createObjectURL(croppedFile);
    if (target === 'new') {
      setNewUserPhoto(croppedFile);
      setNewUserPhotoPreview(previewURL);
    } else if (target === 'edit') {
      setEditPhoto(croppedFile);
      setEditPhotoPreview(previewURL);
    }
  };

  const handleCropCancel = () => {
    const srcToRevoke = cropImageSrc;
    setCropImageSrc(null);
    setCropTarget(null);
    if (srcToRevoke) URL.revokeObjectURL(srcToRevoke);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
   if (!newUser.phone || !newUser.firstName || !newUser.password || !newUser.fitnessStartDate ||
        !newUser.village || !newUser.fitnessLevel || !newUser.dateOfBirth) {
      showMsg('Phone, first name, password, village, level, date of birth and fitness start date are required', 'error');
      return;
    }
    if (!window.confirm(`Create user ${newUser.firstName} ${newUser.lastName}?`)) return;
    setFormLoading(true);
    try {
      const clean = newUser.phone.replace(/\D/g, '').slice(-10);
      const email = `${clean}@spocademy.com`;
      // Create the user on the SECONDARY auth instance so the admin's own
      // session on the primary auth instance is never replaced.
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email, newUser.password);
      const userId = cred.user.uid;
      let photoURL = '';
      if (newUserPhoto) photoURL = await uploadProfilePhoto(userId, newUserPhoto);
      await createUser(userId, {
        phone: `+91${clean}`,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        village: newUser.village,
        fitnessLevel: newUser.fitnessLevel,
        dateOfBirth: newUser.dateOfBirth,
        fitnessStartDate: newUser.fitnessStartDate,
        role: 'user',
        fitnessCurrentDay: 1,
        fitnessPoints: 0,
        streakCount: 0,
        language: 'mr',
        status: 'active',
        unlockedCamps: [],
        attendedCamps: [],
        ...(photoURL ? { photoURL } : {})
      });
      // Sign the secondary instance out so it's clean for the next creation.
      await signOut(secondaryAuth).catch(() => {});
      showMsg(`${newUser.firstName} ${newUser.lastName} created successfully`);
      setNewUser(EMPTY_USER);
      setNewUserPhoto(null);
      setNewUserPhotoPreview(null);
      loadUsers();
    } catch (e) {
      showMsg(e.message, 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // ===== REGISTRATION SHEET FETCH =====

  const handleFetchRegisteredUsers = async () => {
    setFetchingSheet(true);
    setFetchError(null);
    try {
      const res = await fetch(REG_SHEET_CSV_URL);
      if (!res.ok) throw new Error('sheet unreachable');
      const csvText = await res.text();
      const rows = parseCSV(csvText);
      const existingPhones = new Set(
        users.map(u => (u.phone || '').replace(/\D/g, '').slice(-10))
      );
      const dataRows = rows.slice(1).filter(r => r.length > 3 && r[3]);
      const mapped = dataRows
        .map((r, idx) => {
          const phoneClean = (r[3] || '').replace(/\D/g, '').slice(-10);
          return {
            id: `row-${idx}-${phoneClean}`,
            firstName: (r[1] || '').trim(),
            lastName: (r[2] || '').trim(),
            phone: phoneClean,
            dateOfBirth: parseSheetDate(r[4] || ''),
            village: (r[5] || '').trim(),
            fitnessLevel: 'beginnerBoys',
            fitnessStartDate: getIndiaDateString(),
            password: generatePasswordFromPhone(phoneClean),
            showVillageInput: false,
            newVillageText: '',
            creating: false,
          };
        })
        .filter(r => r.phone.length === 10 && !existingPhones.has(r.phone));

      setSkippedCount(dataRows.length - mapped.length);
      setFetchedRows(mapped);
    } catch (e) {
      setFetchError('Could not load the registration sheet. Check your internet connection and try again.');
    } finally {
      setFetchingSheet(false);
    }
  };

  const updateFetchedRow = (id, changes) => {
    setFetchedRows(prev => prev.map(r => r.id === id ? { ...r, ...changes } : r));
  };

  const handleAddRowVillage = (row) => {
    const v = row.newVillageText.trim();
    if (!v) return;
    if (villages.includes(v)) { showMsg('Village already exists', 'error'); return; }
    const updated = [...villages, v].sort();
    setVillages(updated);
    updateFetchedRow(row.id, { village: v, newVillageText: '', showVillageInput: false });
  };

  const handleCreateFromRow = async (row) => {
    if (!row.phone || !row.firstName || !row.password || !row.village || !row.fitnessLevel ||
        !row.dateOfBirth || !row.fitnessStartDate) {
      showMsg('All fields are required to create this user', 'error');
      return;
    }
    if (!window.confirm(`Create user ${row.firstName} ${row.lastName}?`)) return;
    updateFetchedRow(row.id, { creating: true });
    try {
      const email = `${row.phone}@spocademy.com`;
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email, row.password);
      const userId = cred.user.uid;
      await createUser(userId, {
        phone: `+91${row.phone}`,
        firstName: row.firstName,
        lastName: row.lastName,
        village: row.village,
        fitnessLevel: row.fitnessLevel,
        dateOfBirth: row.dateOfBirth,
        fitnessStartDate: row.fitnessStartDate,
        role: 'user',
        fitnessCurrentDay: 1,
        fitnessPoints: 0,
        streakCount: 0,
        language: 'mr',
        status: 'active',
        unlockedCamps: [],
        attendedCamps: [],
      });
      await signOut(secondaryAuth).catch(() => {});
      showMsg(`${row.firstName} ${row.lastName} created successfully`);
      setFetchedRows(prev => prev.filter(r => r.id !== row.id));
      loadUsers();
    } catch (e) {
      showMsg(e.message, 'error');
      updateFetchedRow(row.id, { creating: false });
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'inactive' ? 'active' : 'inactive';
    const note = newStatus === 'inactive'
      ? '\n\nThey will stop receiving notifications.'
      : '\n\nThey will be asked to allow notifications again.';
    if (!window.confirm(`Mark ${getFullName(user)} as ${newStatus.toUpperCase()}?${note}`)) return;
    try {
      if (newStatus === 'inactive') {
        await updateUser(user.id, { status: newStatus });
        await deletePushSubscription(user.id).catch(() => {});
      } else {
        await updateUser(user.id, { status: newStatus, notificationPromptStatus: 'never_asked' });
      }
      showMsg(`${getFullName(user)} marked as ${newStatus}`);
      loadUsers();
    } catch (e) {
      showMsg('Error updating status', 'error');
    }
  };

  const handleEditClick = (user) => {
    setDetailsUserId(null);
    if (editingUserId === user.id) {
      setEditingUserId(null);
      setEditPhoto(null);
      setEditPhotoPreview(null);
      setResetPassword('');
      setShowEditVillageInput(false);
      setNewEditVillage('');
      setShowPauseInput(false);
      setPauseReason('');
      return;
    }
    setEditingUserId(user.id);
    setEditPhoto(null);
    setEditPhotoPreview(null);
    setResetPassword('');
    setShowEditVillageInput(false);
    setNewEditVillage('');
    setShowPauseInput(false);
    setPauseReason('');
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      village: user.village || '',
      fitnessLevel: user.fitnessLevel || 'beginnerBoys',
      dateOfBirth: user.dateOfBirth || '',
      fitnessStartDate: user.fitnessStartDate || '',
      photoURL: user.photoURL || '',
    });
  };

  const handleDetailsClick = async (user) => {
    setEditingUserId(null);
    if (detailsUserId === user.id) {
      setDetailsUserId(null);
      return;
    }
    setDetailsUserId(user.id);
    if (!testResultsByUser[user.id]) {
      setTestResultsLoadingId(user.id);
      try {
        const results = await getLekhiTestResultsForUser(user.id);
        const sorted = results.slice().sort((a, b) => (a.testNumber || 0) - (b.testNumber || 0));
        setTestResultsByUser(prev => ({ ...prev, [user.id]: sorted }));
      } catch (e) {
      } finally {
        setTestResultsLoadingId(null);
      }
    }
  };

  const handleEditSave = async (user) => {
    const levelChanged = editForm.fitnessLevel !== user.fitnessLevel;
    const confirmMsg = levelChanged
      ? `Save changes for ${getFullName(user)}? Fitness level has changed — user's training day will reset to Day 1 of the new level.`
      : `Save changes for ${getFullName(user)}?`;
    if (!window.confirm(confirmMsg)) return;
    setEditPhotoUploading(true);
    try {
      let photoURL = editForm.photoURL;
      if (editPhoto) photoURL = await uploadProfilePhoto(user.id, editPhoto);
      const updates = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        village: editForm.village,
        fitnessLevel: editForm.fitnessLevel,
        dateOfBirth: editForm.dateOfBirth,
        fitnessStartDate: editForm.fitnessStartDate,
        photoURL: photoURL || '',
      };
      if (levelChanged) {
        updates.fitnessCurrentDay = 1;
      }
      await updateUser(user.id, updates);
      showMsg(`${editForm.firstName} ${editForm.lastName} updated successfully`);
      setEditingUserId(null);
      setEditPhoto(null);
      setEditPhotoPreview(null);
      setResetPassword('');
      loadUsers();
    } catch (e) {
      showMsg('Error updating user', 'error');
    } finally {
      setEditPhotoUploading(false);
    }
  };

  const handleResetPassword = async (user) => {
    if (!resetPassword || resetPassword.length < 6) {
      showMsg('Password must be at least 6 characters', 'error'); return;
    }
    if (!window.confirm(`Reset password for ${getFullName(user)}?`)) return;
    setResetLoading(true);
    try {
      const response = await fetch(`/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, newPassword: resetPassword })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed');
      showMsg(`Password reset for ${getFullName(user)}`);
      setResetPassword('');
    } catch (e) {
      showMsg('Error resetting password: ' + e.message, 'error');
    } finally {
      setResetLoading(false);
    }
  };

  const handleRemoveEditPhoto = async (user) => {
    if (!window.confirm(`Remove photo for ${getFullName(user)}?`)) return;
    try {
      await removeProfilePhoto(user.id);
      setEditForm({ ...editForm, photoURL: '' });
      setEditPhoto(null);
      setEditPhotoPreview(null);
      showMsg('Photo removed');
      loadUsers();
    } catch (e) {
      showMsg('Error removing photo', 'error');
    }
  };

  const handleUnlockFitness = async (user) => {
    if (!window.confirm(`Unlock fitness for ${getFullName(user)}?`)) return;
    setOverrideLoading(true);
    try {
      await unlockFitnessForUser(user.id);
      showMsg(`Fitness unlocked for ${getFullName(user)}`);
      loadUsers();
    } catch (e) {
      showMsg('Error unlocking fitness: ' + e.message, 'error');
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleUnlockLekhi = async (user) => {
    if (!window.confirm(`Unlock Lekhi for ${getFullName(user)}? Levels will be set to Easy across all subjects.`)) return;
    setOverrideLoading(true);
    try {
      await unlockLekhiForUser(user.id);
      showMsg(`Lekhi unlocked for ${getFullName(user)}`);
      loadUsers();
    } catch (e) {
      showMsg('Error unlocking Lekhi: ' + e.message, 'error');
    } finally {
      setOverrideLoading(false);
    }
  };

  const handlePauseStreakSubmit = async (user) => {
    const trimmedReason = pauseReason.trim();
    if (!trimmedReason) {
      showMsg('Reason is required to pause streak', 'error');
      return;
    }
    if (!window.confirm(`Pause streak today for ${getFullName(user)}?`)) return;
    setOverrideLoading(true);
    try {
      await pauseUserStreak(user.id, getFullName(user), trimmedReason, currentUser?.uid || '');
      showMsg(`Streak paused today for ${getFullName(user)}`);
      setShowPauseInput(false);
      setPauseReason('');
      loadUsers();
    } catch (e) {
      showMsg(e.message || 'Error pausing streak', 'error');
    } finally {
      setOverrideLoading(false);
    }
  };

  const handleLekhiLevelChange = async (user, subject, level) => {
    const current = user[`${subject}Level`];
    if (current === level) return;
    const from = LEKHI_LEVEL_LABELS_MR[current] || '—';
    const to = LEKHI_LEVEL_LABELS_MR[level];
    if (!window.confirm(`Change ${getSubjectName(subject, 'en')} level for ${getFullName(user)} from ${from} to ${to}?\n\nIt applies from the student's next topic.`)) return;
    setOverrideLoading(true);
    try {
      await setLekhiSubjectLevel(user.id, subject, level);
      showMsg(`${getSubjectName(subject, 'en')} level changed to ${to} for ${getFullName(user)}`);
      loadUsers();
    } catch (e) {
      showMsg('Error changing level: ' + e.message, 'error');
    } finally {
      setOverrideLoading(false);
    }
  };

  const isFitnessAlreadyUnlocked = (user) => {
    return (user.attendedCamps || []).length > 0 || user.fitnessOverrideUnlocked === true;
  };

  const isLekhiAlreadyUnlocked = (user) => {
    return user.diagnosticCompleted === true || user.lekhiOverrideUnlocked === true;
  };

  const isStreakPausedToday = (user) => {
    const todayStr = getIndiaDateString();
    return (user.streakPausedDates || []).includes(todayStr);
  };

  const getSorted = () => {
    const q = searchQuery.toLowerCase();
    return users
      .filter(u =>
        u.firstName?.toLowerCase().includes(q) ||
        u.lastName?.toLowerCase().includes(q) ||
        u.village?.toLowerCase().includes(q) ||
        u.phone?.includes(searchQuery)
      )
      .sort((a, b) => {
        let av;
        let bv;
        if (sortConfig.key === 'totalPoints') {
          av = getTotalPoints(a);
          bv = getTotalPoints(b);
        } else if (sortConfig.key === 'lekhiDailyPoints') {
          av = getLekhiDailyPoints(a);
          bv = getLekhiDailyPoints(b);
        } else if (sortConfig.key === 'testPoints') {
          av = getTestPoints(a);
          bv = getTestPoints(b);
        } else if (sortConfig.key === 'daysSinceStart') {
          av = getDaysSinceStart(a) ?? -1;
          bv = getDaysSinceStart(b) ?? -1;
        } else {
          av = a[sortConfig.key] ?? '';
          bv = b[sortConfig.key] ?? '';
        }
        if (av?.seconds) av = av.seconds;
        if (bv?.seconds) bv = bv.seconds;
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
        if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortArrow = (key) => sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';

  const overrideBoxStyle = {
    border: '1.5px solid #f59e0b',
    borderRadius: '8px',
    padding: '14px 16px',
    marginTop: '16px',
    background: '#fffbeb'
  };

  const overrideHeaderStyle = {
    fontSize: '14px',
    fontWeight: 700,
    color: '#92400e',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const overrideBtnStyle = {
    background: '#f59e0b',
    color: '#fff',
    border: 'none',
    padding: '9px 14px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer'
  };

  const overrideBtnDisabledStyle = {
    background: '#e5e7eb',
    color: '#9ca3af',
    border: 'none',
    padding: '9px 14px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'not-allowed'
  };

  const overrideRowStyle = {
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  };

  // ===== DETAILS PANEL STYLES =====
  const detailsRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
    marginBottom: '8px'
  };

  const detailsRowStyle2 = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
    marginBottom: '8px'
  };

  const detailsCellStyle = {
    background: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '8px 10px'
  };

  const detailsLabelStyle = {
    fontSize: '10px',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: '2px'
  };

  const detailsValueStyle = {
    fontSize: '13px',
    color: '#222',
    fontWeight: 600
  };

  const renderDetailsPanel = (user) => {
    const chips = [];
    const diagChip = getDiagnosticChip(user);
    if (diagChip) chips.push(diagChip);
    const testResults = testResultsByUser[user.id];
    const isLoadingTests = testResultsLoadingId === user.id;
    if (testResults) {
      testResults.forEach(r => {
        const total = r.questionLevelData?.length || 60;
        chips.push(`T${r.testNumber}: ${r.score}/${total}`);
      });
    }
    const scoresText = isLoadingTests
      ? 'Loading...'
      : chips.length > 0 ? chips.join(' · ') : '—';

    return (
      <div>
        <div style={detailsRowStyle}>
          {['grammar', 'math', 'reasoning', 'gk'].map(subject => (
            <div key={subject} style={detailsCellStyle}>
              <div style={detailsLabelStyle}>{subject}</div>
              <div style={detailsValueStyle}>{getLekhiLevelDisplay(user, subject)}</div>
            </div>
          ))}
        </div>

        <div style={detailsRowStyle2}>
          <div style={detailsCellStyle}>
            <div style={detailsLabelStyle}>Fitness Adherence</div>
            <div style={detailsValueStyle}>{getFitnessAdherenceText(user)}</div>
          </div>
          <div style={detailsCellStyle}>
            <div style={detailsLabelStyle}>Lekhi Adherence</div>
            <div style={detailsValueStyle}>{getLekhiAdherenceText(user)}</div>
          </div>
        </div>

        <div style={detailsRowStyle2}>
          <div style={detailsCellStyle}>
            <div style={detailsLabelStyle}>Diagnostic + Tests</div>
            <div style={detailsValueStyle}>{scoresText}</div>
          </div>
          <div style={detailsCellStyle}>
            <div style={detailsLabelStyle}>Camps</div>
            <div style={detailsValueStyle}>{getCampsText(user)}</div>
          </div>
        </div>

        <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
          Last active: {safeDisplay(user.lastActive)}
        </div>
      </div>
    );
  };

  // ===== REGISTRATION ROW RENDER =====

  const renderRegisteredRow = (row) => {
    return (
      <div key={row.id} style={{
        background: '#fff', borderRadius: '10px', padding: '14px',
        marginBottom: '10px', border: '1.5px solid #2563eb33'
      }}>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Phone</label>
            <input type="tel" value={row.phone}
              onChange={e => updateFetchedRow(row.id, { phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} />
          </div>
          <div className="admin-form-group">
            <label>First Name</label>
            <input type="text" value={row.firstName}
              onChange={e => updateFetchedRow(row.id, { firstName: e.target.value })} />
          </div>
          <div className="admin-form-group">
            <label>Last Name</label>
            <input type="text" value={row.lastName}
              onChange={e => updateFetchedRow(row.id, { lastName: e.target.value })} />
          </div>
        </div>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Village</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select value={row.village}
                onChange={e => updateFetchedRow(row.id, { village: e.target.value })}
                style={{ flex: 1 }}>
                <option value="">Select Village</option>
                {villages.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <button type="button" className="admin-btn admin-btn-primary"
                style={{ padding: '9px 14px', fontSize: '18px', lineHeight: 1 }}
                onClick={() => updateFetchedRow(row.id, { showVillageInput: !row.showVillageInput })}>+</button>
            </div>
            {row.showVillageInput && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <input type="text" placeholder="New village name" value={row.newVillageText}
                  onChange={e => updateFetchedRow(row.id, { newVillageText: e.target.value })}
                  style={{ flex: 1, padding: '9px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '15px' }} />
                <button type="button" className="admin-btn admin-btn-primary" onClick={() => handleAddRowVillage(row)}>Add</button>
                <button type="button" className="admin-btn admin-btn-secondary"
                  onClick={() => updateFetchedRow(row.id, { showVillageInput: false, newVillageText: '' })}>✕</button>
              </div>
            )}
          </div>
          <div className="admin-form-group">
            <label>Level</label>
            <select value={row.fitnessLevel}
              onChange={e => updateFetchedRow(row.id, { fitnessLevel: e.target.value })}>
              {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>
        </div>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label>Date of Birth</label>
            <input type="date" value={row.dateOfBirth}
              onChange={e => updateFetchedRow(row.id, { dateOfBirth: e.target.value })} />
          </div>
          <div className="admin-form-group">
            <label>Fitness Start Date</label>
            <input type="date" value={row.fitnessStartDate}
              onChange={e => updateFetchedRow(row.id, { fitnessStartDate: e.target.value })} />
          </div>
          <div className="admin-form-group">
            <label>Password</label>
            <input type="text" value={row.password}
              onChange={e => updateFetchedRow(row.id, { password: e.target.value })} />
          </div>
        </div>
        <button type="button" className="admin-btn admin-btn-primary"
          disabled={row.creating}
          onClick={() => handleCreateFromRow(row)}>
          {row.creating ? 'Creating...' : 'Create User'}
        </button>
      </div>
    );
  };

  return (
    <div>
      <div className="admin-section-title">Users</div>

      <div className="admin-card">
        <h3>Add New User</h3>
        <form onSubmit={handleCreateUser}>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Phone Number *</label>
              <input type="tel" placeholder="9876543210"
                value={newUser.phone}
                onChange={e => setNewUser({ ...newUser, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} />
            </div>
            <div className="admin-form-group">
              <label>First Name *</label>
              <input type="text" placeholder="Rahul"
                value={newUser.firstName}
                onChange={e => setNewUser({ ...newUser, firstName: e.target.value })} />
            </div>
            <div className="admin-form-group">
              <label>Last Name</label>
              <input type="text" placeholder="Patil"
                value={newUser.lastName}
                onChange={e => setNewUser({ ...newUser, lastName: e.target.value })} />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Village *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select value={newUser.village}
                  onChange={e => setNewUser({ ...newUser, village: e.target.value })}
                  style={{ flex: 1 }}>
                  <option value="">Select Village</option>
                  {villages.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <button type="button" className="admin-btn admin-btn-primary"
                  style={{ padding: '9px 14px', fontSize: '18px', lineHeight: 1 }}
                  onClick={() => setShowVillageInput(!showVillageInput)}>+</button>
              </div>
              {showVillageInput && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <input type="text" placeholder="New village name" value={newVillage}
                    onChange={e => setNewVillage(e.target.value)}
                    style={{ flex: 1, padding: '9px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '15px' }} />
                  <button type="button" className="admin-btn admin-btn-primary" onClick={handleAddVillage}>Add</button>
                  <button type="button" className="admin-btn admin-btn-secondary"
                    onClick={() => { setShowVillageInput(false); setNewVillage(''); }}>✕</button>
                </div>
              )}
            </div>
            <div className="admin-form-group">
              <label>Level *</label>
              <select value={newUser.fitnessLevel} onChange={e => setNewUser({ ...newUser, fitnessLevel: e.target.value })}>
                {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Date of Birth *</label>
              <input type="date" value={newUser.dateOfBirth}
                onChange={e => setNewUser({ ...newUser, dateOfBirth: e.target.value })} />
            </div>
            <div className="admin-form-group">
              <label>Fitness Start Date *</label>
              <input type="date" value={newUser.fitnessStartDate}
                onChange={e => setNewUser({ ...newUser, fitnessStartDate: e.target.value })} />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Password *</label>
              <input type="text" placeholder="Set password" value={newUser.password}
                onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
            </div>
            <div className="admin-form-group">
              <label>Profile Photo (optional, max 10MB)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {newUserPhotoPreview && (
                  <img src={newUserPhotoPreview} alt="preview"
                    style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                )}
                <button type="button" className="admin-btn admin-btn-secondary"
                  onClick={() => newUserPhotoRef.current?.click()}>
                  {newUserPhotoPreview ? 'Change Photo' : 'Upload Photo'}
                </button>
                {newUserPhotoPreview && (
                  <button type="button" className="admin-btn admin-btn-danger"
                    style={{ padding: '6px 12px', fontSize: '13px' }}
                    onClick={() => { setNewUserPhoto(null); setNewUserPhotoPreview(null); }}>
                    Remove
                  </button>
                )}
              </div>
              <input ref={newUserPhotoRef} type="file" accept="image/*"
                style={{ display: 'none' }} onChange={handleNewUserPhotoChange} />
            </div>
          </div>
          {statusMsg && <div className={`admin-status-msg ${statusMsg.type}`}>{statusMsg.text}</div>}
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={formLoading}>
              {formLoading ? 'Creating...' : 'Create User'}
            </button>
            <button type="button" className="admin-btn admin-btn-secondary"
              onClick={handleFetchRegisteredUsers} disabled={fetchingSheet}>
              {fetchingSheet ? 'Fetching...' : 'Fetch Registered Users'}
            </button>
          </div>
        </form>
      </div>

      {fetchedRows !== null && (
        <div className="admin-card">
          <h3>New Registrations ({fetchedRows.length})</h3>
          {fetchError && (
            <div className="admin-status-msg error" style={{ marginBottom: '12px' }}>{fetchError}</div>
          )}
          {!fetchError && skippedCount > 0 && (
            <p style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
              {skippedCount} already registered — not shown.
            </p>
          )}
          {!fetchError && fetchedRows.length === 0 && (
            <p style={{ color: '#999' }}>No new registrations found.</p>
          )}
          {fetchedRows.map(row => renderRegisteredRow(row))}
        </div>
      )}

      <div className="admin-card">
        <h3>All Users ({users.length})</h3>
        <input type="text" placeholder="Search by name, village or phone..."
          value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          style={{ width: '100%', padding: '10px 13px', border: '1px solid #ddd', borderRadius: '8px',
            fontSize: '15px', marginBottom: '16px', boxSizing: 'border-box' }} />
        {loading ? <p>लोड होत आहे...</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('firstName')} style={{ cursor: 'pointer' }}>Name{sortArrow('firstName')}</th>
                  <th onClick={() => handleSort('village')} style={{ cursor: 'pointer' }}>Village{sortArrow('village')}</th>
                  <th onClick={() => handleSort('fitnessLevel')} style={{ cursor: 'pointer' }}>Level{sortArrow('fitnessLevel')}</th>
                  <th onClick={() => handleSort('fitnessPoints')} style={{ cursor: 'pointer' }}>Fitness Points{sortArrow('fitnessPoints')}</th>
                  <th onClick={() => handleSort('lekhiDailyPoints')} style={{ cursor: 'pointer' }}>Lekhi Points{sortArrow('lekhiDailyPoints')}</th>
                  <th onClick={() => handleSort('testPoints')} style={{ cursor: 'pointer' }}>Test Points{sortArrow('testPoints')}</th>
                  <th onClick={() => handleSort('totalPoints')} style={{ cursor: 'pointer' }}>Total Points{sortArrow('totalPoints')}</th>
                  <th onClick={() => handleSort('streakCount')} style={{ cursor: 'pointer' }}>Streak{sortArrow('streakCount')}</th>
                  <th onClick={() => handleSort('fitnessStartDate')} style={{ cursor: 'pointer' }}>Batch Start{sortArrow('fitnessStartDate')}</th>
                  <th onClick={() => handleSort('daysSinceStart')} style={{ cursor: 'pointer' }}>Days Since Start{sortArrow('daysSinceStart')}</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getSorted().map(user => (
                  <React.Fragment key={user.id}>
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {user.photoURL ? (
                            <img src={user.photoURL} alt={getFullName(user)}
                              style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e9ecef', flexShrink: 0 }} />
                          )}
                          <div>
                            <div style={{ fontWeight: 600 }}>{getFullName(user)}</div>
                            <div style={{ fontSize: '12px', color: '#999' }}>{safeDisplay(user.phone)}</div>
                          </div>
                        </div>
                      </td>
                      <td>{safeDisplay(user.village)}</td>
                      <td style={{ fontSize: '13px' }}>{safeDisplay(user.fitnessLevel)}</td>
                      <td>{safeDisplay(user.fitnessPoints || 0)}</td>
                      <td>{safeDisplay(getLekhiDailyPoints(user))}</td>
                      <td>{safeDisplay(getTestPoints(user))}</td>
                      <td>{safeDisplay(getTotalPoints(user))}</td>
                      <td>{safeDisplay(user.streakCount || 0)}</td>
                      <td style={{ fontSize: '13px' }}>{safeDisplay(user.fitnessStartDate)}</td>
                      <td>{safeDisplay(getDaysSinceStart(user))}</td>
                      <td>
                        <button
                          className={`admin-btn ${user.status === 'inactive' ? 'admin-btn-danger' : 'admin-btn-success'}`}
                          style={{ padding: '4px 10px', fontSize: '13px' }}
                          onClick={() => handleToggleStatus(user)}>
                          {user.status === 'inactive' ? 'Inactive' : 'Active'}
                        </button>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button className="admin-btn admin-btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '13px' }}
                            onClick={() => handleEditClick(user)}>
                            {editingUserId === user.id ? 'Cancel' : 'Edit'}
                          </button>
                          <button className="admin-btn admin-btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '13px' }}
                            onClick={() => handleDetailsClick(user)}>
                            {detailsUserId === user.id ? 'Hide' : 'Details'}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {detailsUserId === user.id && (
                      <tr>
                        <td colSpan={12} style={{ background: '#f8f9fa', padding: '12px 16px' }}>
                          {renderDetailsPanel(user)}
                        </td>
                      </tr>
                    )}

                    {editingUserId === user.id && (
                      <tr>
                        <td colSpan={12} style={{ background: '#f8f9fa', padding: '16px' }}>
                          <div className="admin-form-row">
                            <div className="admin-form-group">
                              <label>First Name</label>
                              <input type="text" value={editForm.firstName}
                                onChange={e => setEditForm({ ...editForm, firstName: e.target.value })} />
                            </div>
                            <div className="admin-form-group">
                              <label>Last Name</label>
                              <input type="text" value={editForm.lastName}
                                onChange={e => setEditForm({ ...editForm, lastName: e.target.value })} />
                            </div>
                            <div className="admin-form-group">
                              <label>Village</label>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <select value={editForm.village}
                                  onChange={e => setEditForm({ ...editForm, village: e.target.value })}
                                  style={{ flex: 1 }}>
                                  <option value="">Select Village</option>
                                  {villages.map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                                <button type="button" className="admin-btn admin-btn-primary"
                                  style={{ padding: '9px 14px', fontSize: '18px', lineHeight: 1 }}
                                  onClick={() => setShowEditVillageInput(!showEditVillageInput)}>+</button>
                              </div>
                              {showEditVillageInput && (
                                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                  <input type="text" placeholder="New village name" value={newEditVillage}
                                    onChange={e => setNewEditVillage(e.target.value)}
                                    style={{ flex: 1, padding: '9px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '15px' }} />
                                  <button type="button" className="admin-btn admin-btn-primary" onClick={handleAddEditVillage}>Add</button>
                                  <button type="button" className="admin-btn admin-btn-secondary"
                                    onClick={() => { setShowEditVillageInput(false); setNewEditVillage(''); }}>✕</button>
                                </div>
                              )}
                            </div>
                            <div className="admin-form-group">
                              <label>Level</label>
                              <select value={editForm.fitnessLevel}
                                onChange={e => setEditForm({ ...editForm, fitnessLevel: e.target.value })}>
                                {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                              </select>
                            </div>
                          </div>
                          <div className="admin-form-row">
                            <div className="admin-form-group">
                              <label>Date of Birth</label>
                              <input type="date" value={editForm.dateOfBirth}
                                onChange={e => setEditForm({ ...editForm, dateOfBirth: e.target.value })} />
                            </div>
                            <div className="admin-form-group">
                              <label>Fitness Start Date</label>
                              <input type="date" value={editForm.fitnessStartDate}
                                onChange={e => setEditForm({ ...editForm, fitnessStartDate: e.target.value })} />
                            </div>
                            <div className="admin-form-group">
                              <label>Profile Photo (max 10MB)</label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                {(editPhotoPreview || editForm.photoURL) && (
                                  <img src={editPhotoPreview || editForm.photoURL} alt="preview"
                                    style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                )}
                                <button type="button" className="admin-btn admin-btn-secondary"
                                  onClick={() => editPhotoRef.current?.click()}>
                                  {editForm.photoURL || editPhotoPreview ? 'Change Photo' : 'Upload Photo'}
                                </button>
                                {(editForm.photoURL || editPhotoPreview) && (
                                  <button type="button" className="admin-btn admin-btn-danger"
                                    style={{ padding: '6px 12px', fontSize: '13px' }}
                                    onClick={() => handleRemoveEditPhoto(user)}>
                                    Remove Photo
                                  </button>
                                )}
                              </div>
                              <input ref={editPhotoRef} type="file" accept="image/*"
                                style={{ display: 'none' }} onChange={handleEditPhotoChange} />
                            </div>
                          </div>

                          {/* Reset Password */}
                          <div className="admin-form-row">
                            <div className="admin-form-group">
                              <label>Reset Password (leave blank to keep current)</label>
                              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input type="password" placeholder="New password (min 6 chars)"
                                  value={resetPassword}
                                  onChange={e => setResetPassword(e.target.value)}
                                  style={{ flex: 1 }} />
                                <button type="button" className="admin-btn admin-btn-secondary"
                                  style={{ padding: '9px 14px', fontSize: '13px', whiteSpace: 'nowrap' }}
                                  onClick={() => handleResetPassword(user)}
                                  disabled={resetLoading}>
                                  {resetLoading ? 'Resetting...' : 'Reset Password'}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Lekhi levels */}
                          <div style={{ border: '1.5px solid #c7cbf7', borderRadius: '8px', padding: '14px 16px', marginTop: '16px', background: '#f7f7ff' }}>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#4f51d4', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Lekhi levels
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                              {['grammar', 'math', 'reasoning', 'gk'].map(s => (
                                <div key={s}>
                                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{getSubjectName(s, 'en')}</div>
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    {['easy', 'medium', 'hard'].map(l => {
                                      const active = user[`${s}Level`] === l;
                                      return (
                                        <button key={l} type="button" disabled={overrideLoading}
                                          onClick={() => handleLekhiLevelChange(user, s, l)}
                                          style={{
                                            flex: 1, padding: '7px 4px', borderRadius: '6px', cursor: 'pointer',
                                            border: active ? '1.5px solid #6366f1' : '1.5px solid #ddd',
                                            background: active ? '#6366f1' : '#fff',
                                            color: active ? '#fff' : '#333', fontSize: '13px', fontWeight: 600
                                          }}>
                                          {LEKHI_LEVEL_LABELS_MR[l]}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

{/* Overrides Section */}
                          <div style={overrideBoxStyle}>
                            <div style={overrideHeaderStyle}>Overrides</div>

                            <div style={overrideRowStyle}>
                              {isFitnessAlreadyUnlocked(user) ? (
                                <button type="button" style={overrideBtnDisabledStyle} disabled>
                                  Fitness already unlocked
                                </button>
                              ) : (
                                <button type="button" style={overrideBtnStyle}
                                  onClick={() => handleUnlockFitness(user)}
                                  disabled={overrideLoading}>
                                  Unlock Fitness
                                </button>
                              )}

                              {isLekhiAlreadyUnlocked(user) ? (
                                <button type="button" style={overrideBtnDisabledStyle} disabled>
                                  Lekhi already unlocked
                                </button>
                              ) : (
                                <button type="button" style={overrideBtnStyle}
                                  onClick={() => handleUnlockLekhi(user)}
                                  disabled={overrideLoading}>
                                  Unlock Lekhi
                                </button>
                              )}

                              {isStreakPausedToday(user) ? (
                                <button type="button" style={overrideBtnDisabledStyle} disabled>
                                  Streak paused today
                                </button>
                              ) : (
                                <button type="button" style={overrideBtnStyle}
                                  onClick={() => setShowPauseInput(!showPauseInput)}
                                  disabled={overrideLoading}>
                                  Pause Streak Today
                                </button>
                              )}
                            </div>

                            {showPauseInput && !isStreakPausedToday(user) && (
                              <div style={{ marginTop: '10px' }}>
                                <textarea
                                  placeholder="Reason for pause (required)"
                                  value={pauseReason}
                                  onChange={e => setPauseReason(e.target.value)}
                                  rows={2}
                                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd',
                                    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
                                    fontFamily: 'inherit', resize: 'vertical' }} />
                                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                                  <button type="button" style={overrideBtnStyle}
                                    onClick={() => handlePauseStreakSubmit(user)}
                                    disabled={overrideLoading}>
                                    {overrideLoading ? 'Pausing...' : 'Submit Pause'}
                                  </button>
                                  <button type="button"
                                    style={{ ...overrideBtnStyle, background: '#9ca3af' }}
                                    onClick={() => { setShowPauseInput(false); setPauseReason(''); }}
                                    disabled={overrideLoading}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          <button className="admin-btn admin-btn-primary"
                            style={{ marginTop: '16px' }}
                            onClick={() => handleEditSave(user)} disabled={editPhotoUploading}>
                            {editPhotoUploading ? 'Saving...' : 'Save Changes'}
                          </button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cropImageSrc && (
        <ImageCropper
          imageSrc={cropImageSrc}
          language="mr"
          onCancel={handleCropCancel}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
}